/* eslint-disable */
import {
  first_nested_value,
  first_value,
  normalize_text
} from '@/services/onboarding_data_helpers';
import {
  extract_saved_answers,
  normalize_main_section
} from '@/services/onboarding_form_helpers';
import {
  GATE_ROUTE_ALIASES,
  GATE_ROUTE_CONFIG
} from '@/components/config_components/ConfigSidebar/config_sidebar_constants';

const get_name_words = (name) => {
  const normalized_name = String(name || '').trim();
  return normalized_name ? normalized_name.split(/\s+/).filter(Boolean) : [];
};

export const get_initials = (name) => {
  const words = get_name_words(name);
  if (!words.length) return '--';

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

const get_answer_text = (answer) => first_value([
  answer?.answer,
  answer?.value,
  answer?.response,
  answer?.display_value,
  answer?.file_name,
  answer?.file_url
], 'Not answered');

const get_question_text = (answer) => first_value([
  answer?.question,
  answer?.question_text,
  answer?.label,
  answer?.field_label,
  first_nested_value(answer, ['question.question_text', 'question.question'])
], 'Question');

const get_answer_tone = (answer_text) => {
  const normalized = normalize_text(answer_text);

  if (['no', 'failed', 'expired', 'rejected', 'blocked'].some((word) => normalized.includes(word))) {
    return 'warning';
  }

  if (['yes', 'passed', 'verified', 'approved', 'complete', 'signed'].some((word) => normalized.includes(word))) {
    return 'success';
  }

  return 'secondary';
};

const normalize_form_answers = (profile) => {
  const form_answers = profile?.form_answers || profile?.answers || [];
  if (Array.isArray(form_answers)) return form_answers;

  return Object.entries(form_answers).map(([key, value]) => {
    if (value && typeof value === 'object') return { question: value.label || value.question || key, ...value };

    return { question: key, answer: value };
  });
};

const normalize_attributes = (profile) => {
  const attributes = [
    ...(Array.isArray(profile?.attributes) ? profile.attributes : []),
    ...(Array.isArray(profile?.current_entity_attributes) ? profile.current_entity_attributes : [])
  ];

  return attributes
    .map((attribute) => ({
      label: first_value([
        attribute?.label,
        attribute?.name,
        attribute?.attribute_name,
        attribute?.question_text,
        first_nested_value(attribute, ['attribute.question_text', 'attribute.name'])
      ], ''),
      value: first_value([
        attribute?.value,
        attribute?.answer,
        attribute?.attribute_value,
        attribute?.display_value
      ], '')
    }))
    .filter((attribute) => attribute.label && attribute.value);
};

const build_summary_row = (label, value) => ({ label, value });
function build_optional_summary_row(label, value) {
  return value ? [build_summary_row(label, value)] : [];
}

function configured_count_label(count) {
  return `${String(count)} configured`;
}

function get_sla_runtimes(case_item = {}) {
  const runtimes = case_item?.onboarding_status?.sla?.runtimes;
  return Array.isArray(runtimes) ? runtimes : [];
}

function get_primary_sla_runtime(case_item = {}) {
  const runtimes = get_sla_runtimes(case_item);
  const open_runtime = runtimes.find((runtime) => String(runtime?.status || '').toLowerCase() !== 'closed');
  return open_runtime || runtimes[0] || null;
}

function format_sla_date(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function build_sla_summary(case_item = {}) {
  const runtime = get_primary_sla_runtime(case_item);
  if (!runtime) return '';

  const name = first_value([runtime?.sla_name], 'SLA');
  const status = first_value([runtime?.status_label, runtime?.status], 'active');
  const due_date = format_sla_date(runtime?.due_at);
  const parts = [name, status, due_date ? `due ${due_date}` : ''];
  return parts.filter(Boolean).join(' - ');
}

const get_case_stage = (case_item) => first_value([
  case_item?.current_workflow_status,
  case_item?.stage,
  case_item?.profile?.current_workflow_status,
  case_item?.profile?.application_status
], '');
const get_workflow_stage_name = (status) => first_value([
  status?.status_name,
  status?.workflow_status_name,
  first_nested_value(status, ['status.status', 'status.name', 'workflow_status.status', 'workflow_status.name']),
  status?.status,
  status?.name,
  status?.label
], '');
const get_workflow_stage_order = (status, fallback_index = 0) => {
  const numeric_order = Number(first_value([
    status?.workflow_status_order,
    status?.order,
    status?.display_order,
    status?.sequence,
    status?.position
  ], Number.NaN));

  return Number.isFinite(numeric_order) ? numeric_order : fallback_index + 1;
};

const get_workflow_order = (status, fallback_index = 0) => {
  const numeric_order = Number(first_value([
    status?.workflow_order,
    status?.entity_workflow_order,
    status?.workflow_sequence
  ], Number.NaN));

  return Number.isFinite(numeric_order) ? numeric_order : fallback_index + 1;
};

function is_initial_workflow_stage(status = {}) {
  return Boolean(first_value([
    status?.is_initial,
    status?.initial,
    status?.isInitial
  ], false));
}

function get_status_workflow_id(status = {}) {
  return String(first_value([
    status?.entity_workflow_id,
    status?.workflow_id,
    status?.workflow?.id
  ], '')).trim();
}

function select_active_workflow_statuses(statuses = [], current_stage_name = '') {
  const items = Array.isArray(statuses) ? statuses : [];
  if (!items.length) return [];

  const grouped_statuses = new Map();
  items.forEach((status, index) => {
    const workflow_id = get_status_workflow_id(status) || '__default__';
    const current_group = grouped_statuses.get(workflow_id) || [];
    current_group.push({
      ...status,
      __workflow_index: index,
      __workflow_stage_name: normalize_text(get_workflow_stage_name(status)),
      __workflow_stage_order: get_workflow_stage_order(status, index),
      __workflow_order: get_workflow_order(status, index)
    });
    grouped_statuses.set(workflow_id, current_group);
  });

  if (grouped_statuses.size <= 1) {
    return items;
  }

  const normalized_current_stage_name = normalize_text(current_stage_name);
  const ranked_groups = Array.from(grouped_statuses.values())
    .map((group) => {
      const sorted_group = [...group].sort((left, right) => {
        if (left.__workflow_order !== right.__workflow_order) {
          return left.__workflow_order - right.__workflow_order;
        }

        return left.__workflow_stage_order - right.__workflow_stage_order;
      });
      const current_index = normalized_current_stage_name
        ? sorted_group.findIndex((status) => status.__workflow_stage_name === normalized_current_stage_name)
        : -1;
      return {
        statuses: sorted_group,
        has_current_stage: current_index >= 0,
        current_index,
        workflow_order: Number(sorted_group[0]?.__workflow_order || Number.MAX_SAFE_INTEGER),
        length: sorted_group.length
      };
    })
    .sort((left, right) => {
      if (left.has_current_stage !== right.has_current_stage) {
        return left.has_current_stage ? -1 : 1;
      }
      if (left.current_index !== right.current_index) {
        return right.current_index - left.current_index;
      }
      if (left.length !== right.length) {
        return right.length - left.length;
      }
      return left.workflow_order - right.workflow_order;
    });

  return (ranked_groups[0]?.statuses || items).map((status) => {
    const next_status = { ...status };
    delete next_status.__workflow_index;
    delete next_status.__workflow_stage_name;
    delete next_status.__workflow_stage_order;
    delete next_status.__workflow_order;
    return next_status;
  });
}

function get_case_workflow_statuses(case_item) {
  const direct_statuses = Array.isArray(case_item?.workflow_statuses)
    ? case_item.workflow_statuses
    : [];
  const runtime_statuses = case_item?.onboarding_status?.workflow?.statuses;
  const runtime_items = Array.isArray(runtime_statuses) ? runtime_statuses : [];
  const configured_stage_names = new Set(
    direct_statuses
      .map((status) => normalize_text(get_workflow_stage_name(status)))
      .filter(Boolean)
  );
  const current_stage_name = normalize_text(get_case_stage(case_item));
  const filtered_runtime_items = configured_stage_names.size
    ? runtime_items.filter((status) => configured_stage_names.has(
      normalize_text(get_workflow_stage_name(status))
    ))
    : runtime_items;
  const runtime_matches_current_stage = current_stage_name
    ? filtered_runtime_items.some((status) => normalize_text(get_workflow_stage_name(status)) === current_stage_name)
    : filtered_runtime_items.length > 0;
  const source_statuses = filtered_runtime_items.length && runtime_matches_current_stage
    ? filtered_runtime_items
    : (direct_statuses.length ? direct_statuses : runtime_items);
  if (!source_statuses.length) return [];
  const active_workflow_statuses = select_active_workflow_statuses(source_statuses, current_stage_name);

  const seen_keys = new Set();
  return active_workflow_statuses.filter((status, index) => {
    const workflow_id = get_status_workflow_id(status);
    const stage_name = normalize_text(get_workflow_stage_name(status));
    const fallback_key = `${workflow_id}:${stage_name}:${String(index)}`;
    const dedupe_key = workflow_id && stage_name ? `${workflow_id}:${stage_name}` : fallback_key;
    if (seen_keys.has(dedupe_key)) {
      return false;
    }

    seen_keys.add(dedupe_key);
    return true;
  });
}

function get_sorted_case_workflow_statuses(case_item = {}) {
  return get_case_workflow_statuses(case_item)
    .map((status, index) => ({
      ...status,
      normalized_name: get_workflow_stage_name(status),
      normalized_order: get_workflow_stage_order(status, index),
      normalized_workflow_order: get_workflow_order(status, index)
    }))
    .filter((status) => status.normalized_name)
    .sort((left, right) => {
      if (left.normalized_workflow_order !== right.normalized_workflow_order) {
        return left.normalized_workflow_order - right.normalized_workflow_order;
      }

      return left.normalized_order - right.normalized_order;
    });
}

function trim_workflow_statuses_to_current_stage(workflow_statuses = [], current_stage_name = '') {
  const normalized_current_stage_name = normalize_text(current_stage_name);
  if (!normalized_current_stage_name) return workflow_statuses;

  const current_stage_index = workflow_statuses.findIndex((status) => (
    normalize_text(status.normalized_name) === normalized_current_stage_name
  ));
  return current_stage_index >= 0
    ? workflow_statuses.slice(0, current_stage_index + 1)
    : workflow_statuses;
}

function get_gate_route_detail_name(gate_id = '') {
  const normalized_gate_id = String(gate_id || '').trim().toLowerCase();
  if (normalized_gate_id === 'verification') return 'OnboardingVerificationCase';
  if (normalized_gate_id === 'field-verification') return 'OnboardingVerificationCase';
  if (normalized_gate_id === 'manual-verification') return 'OnboardingManualVerificationCase';
  if (normalized_gate_id === 'risk-review') return 'OnboardingRiskReviewCase';
  return '';
}

function slugify_gate_key(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function normalize_gate_stage_id(value = '') {
  const normalized_value = normalize_text(String(value || '').replace(/[_-]+/g, ' '));
  const matched_alias = GATE_ROUTE_ALIASES.find((alias) => alias.match(normalized_value));
  return matched_alias?.gate_id || slugify_gate_key(value);
}

export function resolve_gate_id_from_stage_label(value = '') {
  return normalize_gate_stage_id(value);
}

function build_gate_step(gate_id = '', label = '') {
  const route_config = GATE_ROUTE_CONFIG.get(gate_id) || {};
  const detail_route_name = get_gate_route_detail_name(gate_id);

  return {
    gate_id,
    label: label || 'Gate',
    route_name: route_config?.route_name || '',
    detail_route_name,
    can_navigate: Boolean(detail_route_name)
  };
}

function build_workflow_only_step(label = '') {
  const normalized_label = normalize_gate_stage_id(label) || 'workflow-stage';
  return {
    gate_id: normalized_label,
    label: label || 'Stage',
    route_name: '',
    detail_route_name: '',
    can_navigate: false
  };
}

function dedupe_gate_steps(steps = []) {
  const seen_gate_ids = new Set();
  return steps.filter((step) => {
    const gate_id = String(step?.gate_id || '').trim().toLowerCase();
    if (!gate_id || seen_gate_ids.has(gate_id)) {
      return false;
    }

    seen_gate_ids.add(gate_id);
    return true;
  });
}

export function build_case_gate_journey(case_item = {}, current_gate_id = '') {
  const resolved_current_label = resolve_workflow_display_stage(case_item) || get_case_stage(case_item);
  const workflow_statuses = get_sorted_case_workflow_statuses(case_item);
  const workflow_steps = workflow_statuses
    .map((status) => {
      const gate_id = normalize_gate_stage_id(status.normalized_name);
      return gate_id
        ? build_gate_step(gate_id, status.normalized_name)
        : build_workflow_only_step(status.normalized_name);
    })
    .filter(Boolean);

  const resolved_current_gate_id = current_gate_id || normalize_gate_stage_id(resolved_current_label);
  const resolved_current_step = resolved_current_gate_id
    ? build_gate_step(resolved_current_gate_id, resolved_current_label || titleCaseGateId(resolved_current_gate_id))
    : null;
  const deduped_steps = dedupe_gate_steps([
    ...workflow_steps,
    ...(resolved_current_step ? [resolved_current_step] : [])
  ]);
  const normalized_current_label = normalize_text(resolved_current_label);
  const current_step_index = deduped_steps.findIndex((step) => (
    normalize_text(step.label) === normalized_current_label
  ));
  const resolved_current_step_index = current_step_index >= 0
    ? current_step_index
    : deduped_steps.findIndex((step) => step.gate_id === resolved_current_gate_id);

  return deduped_steps.map((step, index) => ({
    ...step,
    state: resolved_current_step_index < 0
      ? (index === 0 ? 'current' : 'upcoming')
      : (index < resolved_current_step_index ? 'completed' : index === resolved_current_step_index ? 'current' : 'upcoming')
  }));
}

function titleCaseGateId(gate_id = '') {
  return String(gate_id || '')
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function build_previous_reviewable_gate(case_item = {}, current_gate_id = '', reviewable_gate_ids = []) {
  const journey_steps = build_case_gate_journey(case_item, current_gate_id);
  const allowed_gate_ids = Array.isArray(reviewable_gate_ids) && reviewable_gate_ids.length
    ? new Set(reviewable_gate_ids.map((gate_id) => String(gate_id || '').trim().toLowerCase()))
    : null;
  const current_index = journey_steps.findIndex((step) => step.state === 'current');

  if (current_index <= 0) {
    return null;
  }

  for (let index = current_index - 1; index >= 0; index -= 1) {
    const step = journey_steps[index];
    if (step?.state !== 'completed') {
      continue;
    }

    if (!allowed_gate_ids || allowed_gate_ids.has(String(step?.gate_id || '').trim().toLowerCase())) {
      return step;
    }

    break;
  }

  return null;
}

export const resolve_workflow_display_stage = (case_item) => {
  const workflow_statuses = get_sorted_case_workflow_statuses(case_item);

  if (!workflow_statuses.length) {
    return get_case_stage(case_item);
  }

  const current_stage_name = normalize_text(get_case_stage(case_item));
  const matching_stage = workflow_statuses.find((status) => (
    normalize_text(status.normalized_name) === current_stage_name
  ));

  if (matching_stage?.normalized_name) {
    return matching_stage.normalized_name;
  }

  const initial_stage = workflow_statuses.find((status) => is_initial_workflow_stage(status));
  if (initial_stage?.normalized_name) {
    return initial_stage.normalized_name;
  }

  // When the application still carries a generic status like "Submitted",
  // use the configured initial workflow stage as the visible gate label.
  return workflow_statuses[0]?.normalized_name || get_case_stage(case_item);
};

export const build_summary_rows = (case_item) => {
  const display_stage = resolve_workflow_display_stage(case_item);
  const template_names = Array.isArray(case_item?.onboarding_template_names)
    ? case_item.onboarding_template_names.join(', ')
    : '';
  const onboarding_status = case_item?.onboarding_status || {};
  const completion = onboarding_status?.completion || {};
  const configured_forms = Array.isArray(
    case_item?.onboarding_config?.data?.form_bindings || case_item?.onboarding_config?.form_bindings
  )
    ? (case_item?.onboarding_config?.data?.form_bindings || case_item?.onboarding_config?.form_bindings)
    : [];
  const status_form_total = Number(first_value([
    onboarding_status?.forms?.expected_forms_total,
    onboarding_status?.forms?.total,
    onboarding_status?.forms?.count
  ], NaN));
  const forms_summary = Number.isFinite(status_form_total)
    ? configured_count_label(status_form_total)
    : configured_forms.length
      ? configured_count_label(configured_forms.length)
      : '';
  const documents_summary = first_value([
    onboarding_status?.documents?.required_count,
    onboarding_status?.documents?.total,
    onboarding_status?.documents?.count
  ], '');
  const compliance_summary = first_value([
    onboarding_status?.compliance?.status,
    onboarding_status?.compliance?.result,
    onboarding_status?.compliance?.overall_status
  ], '');
  const readiness_value = completion?.overall_complete === true
    ? 'Complete'
    : onboarding_status?.parent_complete === true
      ? 'Ready'
      : 'In progress';
  const sla_summary = build_sla_summary(case_item);

  return [
    build_summary_row('Journey', case_item?.journey || '-'),
    build_summary_row('Stage', display_stage || get_case_stage(case_item) || '-'),
    build_summary_row('Owner', case_item?.owner || 'Unassigned'),
    build_summary_row('Risk', case_item?.risk || '-'),
    build_summary_row('Activation', case_item?.activation || '-'),
    ...build_optional_summary_row('Overall readiness', readiness_value),
    ...build_optional_summary_row('Forms', forms_summary),
    ...build_optional_summary_row('Documents', documents_summary ? String(documents_summary) : ''),
    ...build_optional_summary_row('SLA', sla_summary),
    ...build_optional_summary_row('Compliance', compliance_summary),
    ...build_optional_summary_row('Activate by', case_item?.onboarding_activate_by),
    ...build_optional_summary_row('Email', case_item?.onboarding_email),
    ...build_optional_summary_row('Templates', template_names)
  ];
};

export const build_profile_rows = (case_item) => {
  const profile = case_item?.profile || {};
  const rows = [
    { label: 'Name', value: first_value([profile?.name, case_item?.name], '') },
    { label: 'Entity type', value: first_value([profile?.entity_type_name, profile?.entity_type?.entity_type_name, case_item?.journey], '') },
    { label: 'Status', value: first_value([
      profile?.current_workflow_status,
      case_item?.current_workflow_status,
      profile?.application_status,
      case_item?.stage
    ], '') },
    { label: 'Project', value: first_value([profile?.project?.name, profile?.project_name, profile?.project, case_item?.onboarding_project_name], '') },
    { label: 'Owner role', value: first_value([case_item?.onboarding_owner_role_name], '') },
    { label: 'Email', value: first_value([case_item?.onboarding_email], '') }
  ];

  return [...rows, ...normalize_attributes(profile)].filter((row) => row.value);
};

export const build_contact_rows = (case_item) => {
  const profile_roles = case_item?.profile_role || case_item?.profile?.profile_role || [];
  const contacts = Array.isArray(profile_roles) ? profile_roles : [];

  return contacts
    .map((contact) => {
      const user = contact?.user || contact?.assigned_to || contact;
      const first_name = first_value([user?.first_name, contact?.first_name], '');
      const last_name = first_value([user?.last_name, contact?.last_name], '');
      const name = first_value([
        user?.full_name,
        user?.name,
        `${String(first_name)} ${String(last_name)}`.trim(),
        contact?.name
      ], '');
      const email = first_value([user?.email, contact?.email], '');
      const role = first_value([
        contact?.profile_role?.role?.role,
        contact?.profile_role?.role_name,
        contact?.role_name,
        contact?.assigned_role_name
      ], 'Contact');

      return {
        name,
        initials: get_initials(name),
        detail: [role, email].filter(Boolean).join(' · ')
      };
    })
    .filter((contact) => contact.name);
};

export const build_agreement_rows = (case_item) => {
  const documents = Array.isArray(case_item?.documents) ? case_item.documents : [];

  return documents.map((document) => {
    const is_verified = Boolean(document?.is_verified);

    return {
      id: document?.id || document?.file_name || document?.question,
      title: first_value([document?.question, document?.file_name, document?.section], 'Document'),
      detail: first_value([document?.section, document?.submitted_on, document?.file_name], ''),
      status: is_verified ? 'Verified' : 'Submitted',
      tone_class: is_verified ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis',
      icon: is_verified ? 'bi-check-circle' : 'bi-file-earmark-text',
      url: first_value([document?.presigned_url, document?.file_url], '')
    };
  });
};

export const build_questionnaire_answers = (case_item) => {
  return normalize_form_answers(case_item?.profile)
    .filter((answer) => get_question_text(answer) && get_answer_text(answer))
    .slice(0, 8)
    .map((answer) => {
      const answer_text = get_answer_text(answer);

      return {
        question: get_question_text(answer),
        answer: answer_text,
        tone: get_answer_tone(answer_text),
        note: first_value([answer?.note, answer?.comment, answer?.verification_note], ''),
        note_class: 'bg-warning-subtle text-warning-emphasis'
      };
    });
};

const build_saved_answer_maps = (saved_answers = []) => ({
  byQuestionId: new Map(saved_answers.map((answer) => [answer.section_question_id, answer])),
  byQuestionLabel: new Map(
    saved_answers
      .filter((answer) => answer.question_label)
      .map((answer) => [answer.question_label, answer])
  )
});

function apply_saved_answers_to_sections(sections = [], saved_answers = []) {
  const { byQuestionId, byQuestionLabel } = build_saved_answer_maps(saved_answers);

  sections.forEach((main_section) => {
    main_section.sub_sections.forEach((sub_section) => {
      sub_section.questions.forEach((question) => {
        const normalized_question_label = String(question.label || '').trim().toLowerCase();
        const saved_answer = byQuestionId.get(question.id) || byQuestionLabel.get(normalized_question_label);

        if (!saved_answer) {
          return;
        }

        if (question.type === 'multi_select') {
          question.answer = String(saved_answer.answer_text || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          return;
        }

        question.answer = first_value([
          saved_answer.file_name,
          saved_answer.answer_text
        ], question.answer);
        question.file_url = saved_answer.file_url || '';
      });
    });
  });

  return sections;
}

const normalize_question_answer = (question) => {
  if (Array.isArray(question?.answer)) {
    return question.answer.length ? question.answer.join(', ') : 'Not answered';
  }

  const answer = String(question?.answer ?? '').trim();
  return answer || 'Not answered';
};

const build_form_section_answer_counts = (sections = []) => sections.reduce((total, main_section) => (
  total + main_section.sub_sections.reduce((section_total, sub_section) => (
    section_total + sub_section.questions.filter((question) => normalize_question_answer(question) !== 'Not answered').length
  ), 0)
), 0);

export const build_case_form_sections = (case_item) => {
  const detailed_forms = Array.isArray(case_item?.detailed_forms) ? case_item.detailed_forms : [];
  if (!detailed_forms.length) {
    const configured_form_bindings = Array.isArray(
      case_item?.onboarding_config?.data?.form_bindings || case_item?.onboarding_config?.form_bindings
    )
      ? (case_item?.onboarding_config?.data?.form_bindings || case_item?.onboarding_config?.form_bindings)
      : [];
    const preview_forms = Array.isArray(
      case_item?.onboarding_preview?.data?.forms || case_item?.onboarding_preview?.forms
    )
      ? (case_item?.onboarding_preview?.data?.forms || case_item?.onboarding_preview?.forms)
      : [];
    const forms_source = preview_forms.length ? preview_forms : configured_form_bindings;

    return forms_source.map((form, index) => ({
      id: String(first_value([form?.application_form_id, form?.form_id, form?.id], `configured-form-${String(index)}`)),
      form_name: first_value([
        form?.form_name,
        form?.name,
        form?.form?.name,
        form?.form?.form_name
      ], 'Configured form'),
      section_count: 0,
      answered_count: 0,
      sections: []
    }));
  }

  return detailed_forms.map((form) => {
    const saved_answers = extract_saved_answers(form?.application_form);
    const sections = apply_saved_answers_to_sections(
      (Array.isArray(form?.structure) ? form.structure : []).map((item, index) => normalize_main_section(item, index)),
      saved_answers
    );

    return {
      id: String(form?.application_form_id || form?.form_id || form?.id || Math.random()),
      form_name: first_value([form?.form_name, form?.form?.form_name, form?.name], 'Application form'),
      section_count: sections.length,
      answered_count: build_form_section_answer_counts(sections),
      sections: sections.map((main_section) => ({
        id: main_section.id,
        label: main_section.label,
        sub_sections: main_section.sub_sections.map((sub_section) => ({
          id: sub_section.id,
          label: sub_section.label,
          questions: sub_section.questions.map((question) => {
            const saved_answer = saved_answers.find((answer) => answer?.section_question_id === question.id) || null;
            return {
              id: question.id,
              question_id: question.question_id,
              label: question.label,
              answer: normalize_question_answer(question),
              is_mandatory: Boolean(question.is_mandatory),
              type: question.type || 'text',
              answer_raw: question.answer,
              file_url: String(saved_answer?.file_url || '').trim(),
              file_name: String(saved_answer?.file_name || '').trim()
            };
          })
        }))
      }))
    };
  });
};

const build_workflow_timeline_entry = ({
  title,
  detail,
  is_done,
  is_current
}) => ({
  title,
  detail,
  status: is_done ? 'done' : is_current ? 'current' : 'upcoming',
  icon: is_done ? 'bi-check-lg' : is_current ? 'bi-exclamation-lg' : 'bi-dot'
});

const build_workflow_stage_timeline = (case_item) => {
  const resolved_current_label = resolve_workflow_display_stage(case_item) || get_case_stage(case_item);
  const workflow_statuses = get_sorted_case_workflow_statuses(case_item)
    .filter((status, index, items) => {
      const previous_status = items[index - 1];
      if (!previous_status) return true;

      return !(
        previous_status.entity_workflow_id === status.entity_workflow_id
        && normalize_text(previous_status.normalized_name) === normalize_text(status.normalized_name)
      );
    });

  if (!workflow_statuses.length) {
    const current_stage = get_case_stage(case_item);
    if (!current_stage) {
      return [];
    }

    return [
      build_workflow_timeline_entry({
        title: current_stage,
        detail: 'Current stage',
        is_done: false,
        is_current: true
      })
    ];
  }

  const current_stage_name = normalize_text(resolved_current_label || get_case_stage(case_item));
  const current_stage_index = workflow_statuses.findIndex((status) => normalize_text(status.normalized_name) === current_stage_name);
  const workflow_is_ready = case_item?.activation_tone === 'ready' || case_item?.workflow_complete === true;

  return workflow_statuses.map((status, index) => {
    const is_current = current_stage_index >= 0
      ? index === current_stage_index
      : (!workflow_is_ready && index === 0);
    const is_done = workflow_is_ready
      ? true
      : current_stage_index >= 0
        ? index < current_stage_index
        : false;

    return build_workflow_timeline_entry({
      title: status.normalized_name,
      detail: is_done ? 'Completed' : is_current ? 'Current stage' : 'Upcoming',
      is_done,
      is_current
    });
  });
};

export const build_stage_timeline = (case_item) => build_workflow_stage_timeline(case_item);

export function build_transition_stage_timeline(
  case_item = {},
  transition_history = []
) {
  const current_label = resolve_workflow_display_stage(case_item) || get_case_stage(case_item);
  const current_gate_id = normalize_gate_stage_id(current_label);
  const journey_steps = build_case_gate_journey(case_item, current_gate_id);
  if (!journey_steps.length) {
    return build_workflow_stage_timeline(case_item);
  }

  const current_index = journey_steps.findIndex((step) => (
    String(step?.gate_id || '').trim().toLowerCase() === String(current_gate_id || '').trim().toLowerCase()
  ));
  if (current_index < 0) {
    return build_workflow_stage_timeline(case_item);
  }

  const logs = Array.isArray(transition_history) ? [...transition_history] : [];
  const traversed_gate_ids = new Set();
  if (logs.length) {
    logs
      .sort((left, right) => new Date(left?.date_created || 0).getTime() - new Date(right?.date_created || 0).getTime())
      .forEach((log, index) => {
        if (index === 0) {
          traversed_gate_ids.add(resolve_gate_id_from_stage_label(log?.from_status?.name || ''));
        }
        traversed_gate_ids.add(resolve_gate_id_from_stage_label(log?.to_status?.name || ''));
      });
  }

  return journey_steps.map((step, index) => {
    const is_current = index === current_index;
    const is_done = index < current_index && (
      String(step?.state || '').trim().toLowerCase() === 'completed'
      || traversed_gate_ids.has(String(step?.gate_id || '').trim().toLowerCase())
    );
    return build_workflow_timeline_entry({
      title: step?.label || 'Stage',
      detail: is_done ? 'Completed' : is_current ? 'Current stage' : 'Upcoming',
      is_done,
      is_current
    });
  });
}
